import * as R from 'ramda';
import { createSelector } from 'reselect';
// features
// feature new-do
import { makeLocation, getDataFromStops } from '../new-do/helpers';
import { locationFieldsToPick } from '../new-do/settings/fields-settings';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
//////////////////////////////////////////////////

const selectBranchStore = ({ branch }) => branch;
const selectNewDOStore = ({ liteNewDO }) => liteNewDO;
const selectNewDOBranchConfigs = ({ liteNewDO }) => liteNewDO.branchConfigs;

const makeSelectFullDOStore = () => createSelector(
  selectNewDOStore,
  (store) => store,
);

const makeSelectPageType = () => createSelector(
  selectNewDOStore,
  ({ pageType }) => pageType,
);

const makeSelectStops = () => createSelector(
  selectNewDOStore,
  ({ stops }) => stops,
);

const makeSelectLoadType = () => createSelector(
  selectNewDOStore,
  ({ loadType }) => loadType,
);

const makeSelectStopsData = () => createSelector(
  selectNewDOStore,
  ({ stops }) => getDataFromStops(stops),
);

const makeSelectRate = () => createSelector(
  selectNewDOStore,
  ({ rate }) => rate,
);

const makeSelectNumberOfLoads = () => createSelector(
  selectNewDOStore,
  ({ numberOfLoads }) => numberOfLoads,
);

const makeSelectBranchList = () => createSelector(
  selectNewDOStore,
  ({ branchList }) => branchList,
);

const makeSelectBranchInfo = () => createSelector(
  selectNewDOStore,
  ({ branchInfo }) => branchInfo,
);

const makeSelectBranchGuid = () => createSelector(
  selectNewDOStore,
  ({ branchInfo }) => R.path([GC.FIELD_VALUE], branchInfo),
);

const makeSelectDivisionGuid = () => createSelector(
  selectNewDOStore,
  ({ divisionGuid }) => divisionGuid,
);

const makeSelectBranchConfigs = () => createSelector(
  selectNewDOStore,
  ({ branchConfigs }) => branchConfigs,
);

const makeSelectBranchRefTypes = () => createSelector(
  selectNewDOStore,
  ({ branchRefTypes }) => branchRefTypes,
);

const makeSelectPrimaryReference = () => createSelector(
  selectNewDOStore,
  ({ primaryReference }) => primaryReference,
);

const makeSelectCustomerLoadNumber = () => createSelector(
  selectNewDOStore,
  ({ customerLoadNumber }) => customerLoadNumber,
);

const makeSelectLeftActiveTab = () => createSelector(
  selectNewDOStore,
  ({ leftActiveTab }) => leftActiveTab,
);

const makeSelectRightActiveTab = () => createSelector(
  selectNewDOStore,
  ({ rightActiveTab }) => rightActiveTab,
);

const makeSelectReferenceFormData = () => createSelector(
  selectNewDOStore,
  ({ referenceFormData }) => referenceFormData,
);

const makeSelectBillTo = () => createSelector(
  selectNewDOStore,
  ({ billTo }) => billTo,
);

const makeSelectValidationErrors = () => createSelector(
  selectNewDOStore,
  ({ validationErrors }) => validationErrors,
);

const makeSelectValidationWarnings = () => createSelector(
  selectNewDOStore,
  ({ validationWarnings }) => validationWarnings,
);

const makeSelectRatePreviewFiles = () => createSelector(
  selectNewDOStore,
  ({ ratePreviewFiles }) => ratePreviewFiles,
);

const makeSelectTemplateData = () => createSelector(
  selectNewDOStore,
  ({ templateData }) => templateData,
);

const makeSelectTelCreationMode = () => createSelector(
  selectNewDOStore,
  ({ telCreationMode }) => telCreationMode,
);

const makeSelectRouteTelConfigs = () => createSelector(
  selectNewDOBranchConfigs,
  (configs) => G.getRouteTelConfigsForCreateClo(configs),
);

export const createPrimaryReferenceDisplayValue = (sequence, configPrefix, divisionPrefix) => {
  const arrayOfValues = [divisionPrefix, configPrefix, G.createSequenceApproxValue(sequence)];

  return G.createStringFromArray(arrayOfValues, '');
};

const makeSelectPrimaryRefSequenceValue = () => createSelector(
  selectNewDOStore,
  selectBranchStore,
  (doStore, branchStore) => {
    const { branchConfigs, divisionGuid, primaryRefSequence } = doStore;
    const { branchesConfigs } = branchStore;
    const autogenerated = G.getConfigValueFromStore(
      GC.CLO_PRIMARY_REFERENCE_AUTOGENERATED,
      branchConfigs,
    );

    if (R.and(G.isTrue(autogenerated), G.isNotNil(primaryRefSequence))) {
      const configPrefix = G.getConfigValueFromStore(
        GC.CLO_PRIMARY_REFERENCE_PREFIX,
        branchConfigs,
      );
      const configsForDivisionPrefix = G.ifElse(
        G.isNotNilAndNotEmpty(divisionGuid),
        R.pathOr({}, [divisionGuid], branchesConfigs),
        branchConfigs,
      );
      const divisionPrefix = G.getConfigValueFromStore(
        GC.CLO_PRIMARY_REFERENCE_DIVISION_PREFIX,
        configsForDivisionPrefix,
      );

      return createPrimaryReferenceDisplayValue(primaryRefSequence, configPrefix, divisionPrefix);
    }

    return '';
  },
);

const makeSelectPrimaryRefTypeGuid = () => createSelector(
  selectNewDOBranchConfigs,
  (configs) => G.getConfigValueFromStore(
    GC.CLO_PRIMARY_REFERENCE_TYPE,
    configs,
  ),
);

const makeSelectPrimaryRefTypeName = () => createSelector(
  selectNewDOStore,
  (doStore) => {
    const { branchConfigs, branchRefTypes } = doStore;
    const primaryRefTypeGuid = G.getConfigValueFromStore(
      GC.CLO_PRIMARY_REFERENCE_TYPE,
      branchConfigs,
    );

    return R.compose(
      R.path([GC.FIELD_NAME]),
      R.find(R.propEq(primaryRefTypeGuid, GC.FIELD_GUID)),
      R.pathOr([], [GC.LOAD_TYPE_CLO]),
    )(branchRefTypes);
  },
);

const makeSelectCustomerRefTypeName = () => createSelector(
  selectNewDOStore,
  (doStore) => {
    const { branchConfigs, branchRefTypes } = doStore;
    const primaryRefTypeGuid = G.getConfigValueFromStore(
      GC.CLO_GENERAL_CUSTOMER_REFERENCE_TYPE,
      branchConfigs,
    );

    return R.compose(
      R.path([GC.FIELD_NAME]),
      R.find(R.propEq(primaryRefTypeGuid, GC.FIELD_GUID)),
      R.pathOr([], [GC.LOAD_TYPE_CLO]),
    )(branchRefTypes);
  },
);

const getTotalWeight = (stopsData) => {
  const { pickedItems, pickedUpContainers } = stopsData;

  if (G.isNotNilAndNotEmpty(pickedItems)) {
    return G.renameKeys({
      [GC.FIELD_ITEM_WEIGHT]: GC.FIELD_TOTAL_TRIP_WEIGHT,
      [GC.FIELD_ITEM_WEIGHT_TYPE]: GC.FIELD_TOTAL_TRIP_WEIGHT_UOM,
    }, G.calcItemsTotalWeightWithoutQty(pickedItems));
  }

  if (G.isNotNilAndNotEmpty(pickedUpContainers)) {
    return G.renameKeys({
      [GC.FIELD_WEIGHT_UOM]: GC.FIELD_TOTAL_TRIP_WEIGHT_UOM,
      [GC.FIELD_FULL_CONTAINER_WEIGHT]: GC.FIELD_TOTAL_TRIP_WEIGHT,
    }, G.calcContainersTotalWeightWithoutQty(pickedUpContainers));
  }

  return {
    [GC.FIELD_TOTAL_TRIP_WEIGHT]: '',
    [GC.FIELD_TOTAL_TRIP_WEIGHT_UOM]: '',
  };
};

const makeSelectLoadTotals = () => createSelector(
  selectNewDOStore,
  ({ stops }) => {
    const stopsData = getDataFromStops(stops);
    const { sortedStops, pickedItems } = stopsData;
    const totalWeight = getTotalWeight(stopsData);
    const totalQuantity = G.calculateTotalQuantity(pickedItems);
    const totalQuantities = {
      [R.prop(GC.FIELD_ITEM_PACKAGE_TYPE, totalQuantity)]: R.prop(GC.FIELD_ITEM_QUANTITY, totalQuantity),
    };
    const totalVolume = G.calculateTotalVolumeWithoutQty(pickedItems);
    const totalDistance = G.getLoadTotalDistance({ [GC.FIELD_LOAD_STOPS]: sortedStops }, GC.FIELD_DISTANCE_CLO);

    return {
      totalWeight,
      totalVolume,
      totalDistance,
      [GC.FIELD_TOTAL_QUANTITIES]: totalQuantities,
    };
  },
);

const makeSelectMapLocations = () => createSelector(
  selectNewDOStore,
  ({ stops }) => {
    const { sortedStops } = getDataFromStops(stops);

    const mappedStops = R.map((stop) => {
      const { id, order, formData, eventType } = stop;

      const location = makeLocation(R.pick(locationFieldsToPick, formData));
      const contact = R.pick(GC.GROUPED_FIELDS.EVENT_CONTACT_ARR, location);
      const hasNotLatLng = R.or(
        G.isNilOrEmpty(R.pathOr(null, [GC.FIELD_LATITUDE], location)),
        G.isNilOrEmpty(R.pathOr(null, [GC.FIELD_LONGITUDE], location)),
      );

      if (hasNotLatLng) return null;

      return {
        stop,
        contact,
        location,
        guid: id,
        title: `${G.toTitleCase(eventType)} ${order}`,
        latLng: { lat: R.prop(GC.FIELD_LATITUDE, location), lng: R.prop(GC.FIELD_LONGITUDE, location) },
      };
    }, sortedStops);

    return R.compose(R.values(), R.filter((stop) => G.isNotNil(stop)))(mappedStops);
  },
);

const makeSelectDivision = () => createSelector(
  selectNewDOStore,
  selectBranchStore,
  (doStore, branchStore) => {
    const { divisionGuid } = doStore;
    const { treeBranches } = branchStore;

    if (R.or(G.isNilOrEmpty(divisionGuid), G.isNilOrEmpty(treeBranches))) return null;

    return G.getBranchFromTreeByGuid(divisionGuid, treeBranches);
  },
);

const makeSelectSourceType = () => createSelector(
  selectNewDOStore,
  R.pathOr(GC.CREATE_DO_SOURCE_TYPE_MANUAL, [GC.FIELD_SOURCE_TYPE]),
);

const makeSelectSaveAndDuplicateQty = () => createSelector(
  selectNewDOStore,
  ({ saveAndDuplicateQty }) => saveAndDuplicateQty,
);

const makeSelectHotOrder = () => createSelector(
  selectNewDOStore,
  ({ hotOrder }) => hotOrder,
);

const makeSelectDriverRate = () => createSelector(
  selectNewDOStore,
  ({ telDriverRate }) => telDriverRate,
);

const makeSelectRouteTemplateGuid = () => createSelector(
  selectNewDOStore,
  ({ routeTemplateGuid }) => routeTemplateGuid,
);

const makeSelectStopPointsStrings = () => createSelector(
  selectNewDOStore,
  ({ stopPointsStrings }) => stopPointsStrings,
);

const makeSelectAiRecognize = () => createSelector(
  selectNewDOStore,
  ({ aiRecognize }) => aiRecognize,
);

const makeSelectEtaCalculationResultAndParams = () => createSelector(
  selectNewDOStore,
  R.pick(['etaCalculationResult', 'etaCalculationParams']),
);

const makeSelectETAConfigList = () => createSelector(
  selectNewDOStore,
  ({ etaConfigList }) => etaConfigList,
);

export {
  makeSelectRate,
  makeSelectStops,
  selectNewDOStore,
  makeSelectBillTo,
  makeSelectHotOrder,
  makeSelectDivision,
  makeSelectLoadType,
  makeSelectPageType,
  makeSelectStopsData,
  makeSelectSourceType,
  makeSelectLoadTotals,
  makeSelectBranchList,
  makeSelectBranchInfo,
  makeSelectBranchGuid,
  makeSelectDriverRate,
  makeSelectFullDOStore,
  makeSelectAiRecognize,
  makeSelectDivisionGuid,
  makeSelectTemplateData,
  makeSelectMapLocations,
  makeSelectNumberOfLoads,
  makeSelectLeftActiveTab,
  makeSelectETAConfigList,
  makeSelectBranchConfigs,
  makeSelectRightActiveTab,
  makeSelectBranchRefTypes,
  makeSelectTelCreationMode,
  makeSelectRouteTelConfigs,
  makeSelectRatePreviewFiles,
  makeSelectValidationErrors,
  makeSelectPrimaryReference,
  makeSelectStopPointsStrings,
  makeSelectRouteTemplateGuid,
  makeSelectReferenceFormData,
  makeSelectCustomerLoadNumber,
  makeSelectPrimaryRefTypeGuid,
  makeSelectPrimaryRefTypeName,
  makeSelectValidationWarnings,
  makeSelectCustomerRefTypeName,
  makeSelectSaveAndDuplicateQty,
  makeSelectPrimaryRefSequenceValue,
  makeSelectEtaCalculationResultAndParams,
};
